/**
 * Expedition quests — the chain that keeps an Expedition going.
 *
 * Data + a seeded generator only; the **server** owns every counter and decides when a quest is
 * complete (never trust the client). `nextQuest` is a pure function of (rng, chainIndex, mapDef,
 * playerCount) so a room's whole chain is reproducible from one seed and tests can pin it.
 *
 * Design of record: `docs/EXPEDITION.md`.
 */
import { EntityKind } from './protocol.js';
import type { AreaDef, MapDef } from './maps.js';
import type { Vec3 } from './math.js';

export const QuestKind = {
  /** Kill N Hymns of one kind (optionally only inside an area). */
  Hunt: 1,
  /** Clear every Hymn out of a named area (the set is locked when the quest starts). */
  Purge: 2,
  /** Open N loot caches, or carry out N Hymn Shards. */
  Salvage: 3,
  /** Stand inside an area for T seconds while waves come; the clock stops when nobody is inside. */
  Hold: 4,
  /** Kill a Cantor mid-song (while it is charging). */
  Silence: 5,
  /** Stand inside an area once. The teaching kind: no clock, no waves, arrive and it is done. */
  Reach: 6,
  /** Use one consumable (`QuestStep.itemId` names it). */
  Use: 7,
  /** Leave through an extract. Completed by the server the instant extraction resolves. */
  Extract: 8,
} as const;
export type QuestKindId = (typeof QuestKind)[keyof typeof QuestKind];

/** Short caps label shown next to the chain numeral ("QUEST II · HUNT"). */
export const QUEST_KIND_NAMES: Record<number, string> = {
  [QuestKind.Hunt]: 'HUNT',
  [QuestKind.Purge]: 'PURGE',
  [QuestKind.Salvage]: 'SALVAGE',
  [QuestKind.Hold]: 'HOLD',
  [QuestKind.Silence]: 'SILENCE',
  [QuestKind.Reach]: 'REACH',
  [QuestKind.Use]: 'USE',
  [QuestKind.Extract]: 'EXTRACT',
};

/** What a SALVAGE quest counts. Server-side only — the objective string is what players read. */
export const SalvageTarget = { Cache: 0, Shard: 1 } as const;

/** Breather between a completed quest and the next one being handed out. */
export const QUEST_BREATHER_MS = 5_000;

// ------------------------------------------------------------------ the authored library

/**
 * The settlement voices that hand out quests. Each one is a person with a tone, not a faction
 * label — the brief and the resolution line are speech, and a reader should be able to tell them
 * apart blind. See `quest-library.ts` for `VOICES` and the chains themselves.
 */
export type VoiceId = 'isolde' | 'sabel' | 'wren' | 'ott' | 'pell' | 'choir';
export interface VoiceDef {
  id: VoiceId;
  name: string;
  /** Where they stand in Lanternfall, one clause. */
  role: string;
  /** How they talk, for anyone writing more of them. */
  tone: string;
}

/**
 * One authored rung of a chain. Data only — the goal here is a *base*, scaled at runtime by where
 * the step sits in its chain and by squad size, and `objective` carries `{n}`/`{area}` placeholders
 * the engine substitutes once it knows the scaled numbers.
 */
export interface QuestStep {
  /** `<chainId>_<n>`; unique across the whole library. */
  id: string;
  kind: QuestKindId;
  title: string;
  voice: VoiceId;
  /** Exactly two lines, spoken before the step. */
  brief: [string, string];
  /** Plain words; contains `{n}`, may contain `{area}`. */
  objective: string;
  /** The line that lands when the step completes. */
  line: string;
  goal: number;
  /** HUNT: an `EntityKind`. SALVAGE: a `SalvageTarget`. Unused by the other kinds. */
  target?: number;
  /** USE: the item id the step wants consumed. */
  itemId?: string;
  /** Exact `AreaDef.name` this step wants; resolved against the map, seeded fallback if absent. */
  areaHint?: string;
  /** The last step of a chain: a set piece, with a bigger reward. */
  setPiece?: true;
}

/** Three to five steps that run in order and turn on a twist. */
export interface QuestChain {
  id: string;
  title: string;
  voice: VoiceId;
  /** One line for docs/telemetry: what the twist is. Never shown to players. */
  twist: string;
  steps: QuestStep[];
  /**
   * Restrict the chain to one map. Absent means "any map" — the Spine library. `chainsForMap`
   * is the only thing that reads it, and it is what keeps The Waking out of a real expedition.
   */
  mapId?: string;
  /**
   * A hand-written script rather than a rolled quest: the steps run in fixed order, goals are
   * literal (never scaled by squad size or chain depth), and the payout is whatever the caller
   * decides — `buildQuest` zeroes it, because a tutorial that pays six expedition rungs would
   * hand a brand-new player level 8 and every unlocked tab before they ever see Lanternfall.
   */
  scripted?: true;
}

/** How far one account has got through one chain: the number of steps completed. */
export interface ChainProgress {
  chainId: string;
  /** Steps completed; equals `chain.steps.length` once the chain is finished. */
  step: number;
}

export interface QuestDef {
  /** Stable per-chain id: `q<chainIndex>_<kind>`; unique inside one expedition. */
  id: string;
  kind: QuestKindId;
  /** Short title in the game's voice. */
  title: string;
  /** Plain English, e.g. "Kill 6 Choristers on Deck 1". */
  objective: string;
  /** Units of progress needed. HOLD counts seconds; everything else counts things. */
  goal: number;
  /** HUNT: the `EntityKind` to kill. SALVAGE: a `SalvageTarget`. Unused otherwise. */
  target: number;
  /** USE: the item id that has to be consumed. */
  itemId?: string;
  /** Where the quest happens, when it is bound to a place. */
  area?: AreaDef;
  /** HOLD: the hold duration in seconds (== goal), so the client can render a countdown. */
  timeLimitS?: number;
  /** Paid to every living Waker in the room the moment the quest completes. */
  reward: { wick: number; xp: number };

  // --- authored-library fields (absent on the procedural fallback) ---------------------------
  /** Who is speaking. */
  voice?: VoiceId;
  /** Two lines said before the step. */
  brief?: [string, string];
  /** The line that lands on completion. */
  line?: string;
  /** Chain this step belongs to, and its 0-based position in it. */
  chainId?: string;
  stepIndex?: number;
  /** The chain's last step. */
  setPiece?: true;
  /** Gear pool for this quest's item drop (see `questRewardPool` / `loot.ts`). */
  rewardPool?: 'armor' | 'weapon' | 'any';
  /**
   * From a `QuestChain.scripted` chain: pays nothing and drops nothing. The server reads this to
   * skip the Wick/XP award and the gear roll — see `Room.awardQuest`.
   */
  scripted?: true;
}

// ------------------------------------------------------------------ scaling

/** Squad-size factor: a 4-stack works through roughly 1.75x the goal a solo Waker does. */
export function playerFactor(playerCount: number): number {
  return 0.75 + 0.25 * Math.max(1, Math.min(4, playerCount));
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Reward for a quest at `chainIndex`. Flat per player; the chain, not the squad, scales it. */
export function questReward(chainIndex: number): { wick: number; xp: number } {
  return {
    wick: clamp(60 + 25 * chainIndex, 60, 400),
    xp: clamp(80 + 35 * chainIndex, 80, 600),
  };
}

/**
 * Live-Hymn population the director holds an expedition at. Grows with the chain so quest V is
 * meaningfully louder than quest I, and is hard-capped for the 30 Hz tick budget.
 */
export const DIRECTOR_BASE_PER_PLAYER = 4;
export const DIRECTOR_BASE = 6;
export const DIRECTOR_PER_CHAIN = 2;
export const DIRECTOR_MAX_ALIVE = 26;
export function directorTarget(playerCount: number, chainIndex: number): number {
  const n =
    DIRECTOR_BASE +
    DIRECTOR_BASE_PER_PLAYER * (Math.max(1, playerCount) - 1) +
    DIRECTOR_PER_CHAIN * chainIndex;
  return Math.min(DIRECTOR_MAX_ALIVE, Math.round(n));
}

// ------------------------------------------------------------------ text

const HYMN_NAMES: Record<number, [one: string, many: string]> = {
  [EntityKind.Chorister]: ['Chorister', 'Choristers'],
  [EntityKind.Cantor]: ['Cantor', 'Cantors'],
  [EntityKind.Sexton]: ['Sexton', 'Sextons'],
};
export function hymnName(kind: number, plural: boolean): string {
  const pair = HYMN_NAMES[kind] ?? ['Hymn', 'Hymns'];
  return plural ? pair[1] : pair[0];
}

const HUNT_TITLES = ['Thin the Choir', 'Quiet the Ranks', 'Break the Refrain', 'Cull the Verse'];
const PURGE_TITLES = ['Empty the Room', 'Sweep It Clean', 'Nothing Left Singing'];
const CACHE_TITLES = ['Pick the Bones', 'Strip the Hold', 'Take What Burns'];
const SHARD_TITLES = ['Gather the Voices', 'Fragments', 'Pocket the Song'];
const HOLD_TITLES = ['Stand the Watch', 'Keep the Ground', 'Do Not Move'];
const SILENCE_TITLES = ['Cut the Song Short', 'Break the Note', 'Mid-Verse'];

function pick<T>(rng: () => number, list: readonly T[]): T {
  return list[Math.min(list.length - 1, Math.floor(rng() * list.length))]!;
}

// ------------------------------------------------------------------ generator

/** Kinds unlocked at a given chain index. HUNT/SALVAGE open the chain; the rest layer in. */
function kindsFor(chainIndex: number, hasAreas: boolean): QuestKindId[] {
  const kinds: QuestKindId[] = [QuestKind.Hunt, QuestKind.Salvage];
  if (chainIndex >= 1) kinds.push(QuestKind.Silence);
  if (chainIndex >= 1 && hasAreas) kinds.push(QuestKind.Purge);
  if (chainIndex >= 2 && hasAreas) kinds.push(QuestKind.Hold);
  return kinds;
}

/**
 * Roll the quest at `chainIndex`. Deterministic in `rng`: the same generator state and arguments
 * always yield the same quest. Never returns an area-bound kind when the map has no areas.
 */
export function nextQuest(
  rng: () => number,
  chainIndex: number,
  mapDef: MapDef,
  playerCount: number,
): QuestDef {
  const areas = mapDef.areas ?? [];
  const kinds = kindsFor(chainIndex, areas.length > 0);
  const kind = pick(rng, kinds);
  const pf = playerFactor(playerCount);
  const reward = questReward(chainIndex);
  const id = `q${chainIndex}_${kind}`;

  switch (kind) {
    case QuestKind.Hunt: {
      const targets: number[] = [EntityKind.Chorister, EntityKind.Chorister, EntityKind.Cantor];
      if (chainIndex >= 2) targets.push(EntityKind.Sexton);
      const target = pick(rng, targets);
      const base =
        target === EntityKind.Sexton ? 1 + Math.floor(chainIndex / 2) : 4 + 2 * chainIndex;
      const goal = clamp(Math.round(base * (target === EntityKind.Sexton ? 1 : pf)), 1, 24);
      // From quest II on, half of all hunts are pinned to a place so the squad has to move.
      const area =
        chainIndex >= 1 && areas.length > 0 && rng() < 0.5 ? pick(rng, areas) : undefined;
      const where = area ? ` in ${area.name}` : '';
      return {
        id,
        kind,
        title: pick(rng, HUNT_TITLES),
        objective: `Kill ${goal} ${hymnName(target, goal !== 1)}${where}`,
        goal,
        target,
        ...(area ? { area } : {}),
        reward,
      };
    }
    case QuestKind.Purge: {
      const area = pick(rng, areas)!;
      const goal = clamp(Math.round((3 + chainIndex) * pf), 3, 12);
      return {
        id,
        kind,
        title: pick(rng, PURGE_TITLES),
        objective: `Clear every Hymn out of ${area.name}`,
        goal,
        target: 0,
        area,
        reward,
      };
    }
    case QuestKind.Salvage: {
      const target = rng() < 0.6 ? SalvageTarget.Cache : SalvageTarget.Shard;
      if (target === SalvageTarget.Cache) {
        const goal = clamp(Math.round((3 + chainIndex) * pf), 3, 12);
        return {
          id,
          kind,
          title: pick(rng, CACHE_TITLES),
          objective: `Open ${goal} loot caches`,
          goal,
          target,
          reward,
        };
      }
      const goal = clamp(Math.round((2 + Math.floor(chainIndex / 2)) * pf), 2, 8);
      return {
        id,
        kind,
        title: pick(rng, SHARD_TITLES),
        objective: `Carry ${goal} Hymn Shards`,
        goal,
        target,
        reward,
      };
    }
    case QuestKind.Hold: {
      const area = pick(rng, areas)!;
      const goal = clamp(45 + 15 * chainIndex, 45, 120);
      return {
        id,
        kind,
        title: pick(rng, HOLD_TITLES),
        objective: `Hold ${area.name} for ${goal} seconds`,
        goal,
        target: 0,
        area,
        timeLimitS: goal,
        reward,
      };
    }
    case QuestKind.Silence:
    default: {
      const goal = clamp(1 + Math.floor(chainIndex / 3), 1, 3);
      return {
        id,
        kind: QuestKind.Silence,
        title: pick(rng, SILENCE_TITLES),
        objective: `Kill ${goal} ${hymnName(EntityKind.Cantor, goal !== 1)} mid-song`,
        goal,
        target: EntityKind.Cantor,
        reward,
      };
    }
  }
}

// ------------------------------------------------------------------ the library engine

/** Chance of continuing a chain the squad already has open, rather than starting a fresh one. */
export const CONTINUE_CHAIN_CHANCE = 0.7;

export interface QuestPick {
  chain: QuestChain;
  step: QuestStep;
  /** 0-based position of `step` inside `chain.steps`. */
  stepIndex: number;
}

export interface PickOptions {
  /** Chains the squad has part-finished, from persisted per-account state. */
  open: readonly ChainProgress[];
  /** Chain ids already finished (never re-offered while anything else is left). */
  done: readonly string[];
  /** The whole library, injectable so tests can pin a small one. */
  library: readonly QuestChain[];
}

/**
 * Choose the next authored step: 70% continue an open chain, 30% start a fresh one, falling back
 * to whichever side is non-empty. Pure and deterministic in `rng` — `open`/`done` are sorted before
 * the roll so two runs with the same seed and the same state always agree, regardless of the
 * iteration order of whatever produced them.
 */
export function pickQuest(rng: () => number, opts: PickOptions): QuestPick | null {
  const byId = new Map(opts.library.map((c) => [c.id, c]));
  const doneSet = new Set(opts.done);

  const openable = [...opts.open]
    .filter((p) => {
      const chain = byId.get(p.chainId);
      return (
        chain !== undefined && p.step > 0 && p.step < chain.steps.length && !doneSet.has(p.chainId)
      );
    })
    .sort(
      (a, b) => b.step - a.step || (a.chainId < b.chainId ? -1 : a.chainId > b.chainId ? 1 : 0),
    );

  const startedIds = new Set(opts.open.filter((p) => p.step > 0).map((p) => p.chainId));
  const fresh = opts.library.filter((c) => !doneSet.has(c.id) && !startedIds.has(c.id));

  const continueIt = openable.length > 0 && (fresh.length === 0 || rng() < CONTINUE_CHAIN_CHANCE);
  if (continueIt) {
    const p = openable[Math.floor(rng() * openable.length)] ?? openable[0]!;
    const chain = byId.get(p.chainId)!;
    return { chain, step: chain.steps[p.step]!, stepIndex: p.step };
  }
  if (fresh.length > 0) {
    const chain = fresh[Math.floor(rng() * fresh.length)] ?? fresh[0]!;
    const step = chain.steps[0];
    if (step) return { chain, step, stepIndex: 0 };
  }
  // Everything is finished: replay the library from the top rather than running out of quests.
  const chain = opts.library[Math.floor(rng() * Math.max(1, opts.library.length))];
  const step = chain?.steps[0];
  return chain && step ? { chain, step, stepIndex: 0 } : null;
}

/**
 * Which gear pool a quest's item reward comes from. Fed to `questRewardRoll` in `loot.ts` (the
 * armour / weapon-upgrade ladder), which is the single seam the server calls — widening the ladder
 * needs no change here or in `room.ts`. Fights pay in weapons, ground you held pays in armour,
 * salvage pays in whatever turns up.
 */
export function questRewardPool(kind: QuestKindId): 'armor' | 'weapon' | 'any' {
  if (kind === QuestKind.Hunt || kind === QuestKind.Silence) return 'weapon';
  if (kind === QuestKind.Hold || kind === QuestKind.Purge) return 'armor';
  return 'any';
}

/** Kinds the server resolves from a position sample rather than from an event. */
export function isAreaArrivalKind(kind: number): boolean {
  return kind === QuestKind.Reach;
}

/** Set pieces pay double. */
export const SET_PIECE_REWARD_MULT = 2;

/**
 * What `{area}` reads as when a step's `areaHint` names ground this map does not have.
 *
 * The quest library is written once and drawn from by every map, so a chain authored against the
 * Spine's "the Cargo Hold" has to say *something* sensible when it is rolled in the Vigil Downs.
 * It used to say "the wreck" — a hardcoded string that was wrong the moment a second map existed,
 * and actively misleading in a zone with no ship in it. Now the map answers for itself: its own
 * `defaultAreaName`, or the first ground it has a name for, or — for a map with no named areas at
 * all — the map's own name, which is never wrong even if it is vague.
 */
export function defaultAreaName(mapDef: MapDef): string {
  return mapDef.defaultAreaName ?? mapDef.areas?.[0]?.name ?? mapDef.name;
}

/**
 * Turn an authored step into the live quest: resolve its area against this map, scale its goal by
 * where it sits in its chain and by squad size, and fill the objective's placeholders.
 */
export function buildQuest(
  pick: QuestPick,
  chainIndex: number,
  mapDef: MapDef,
  playerCount: number,
  rng: () => number,
): QuestDef {
  const { chain, step, stepIndex } = pick;
  const areas = mapDef.areas ?? [];
  const needsArea =
    step.kind === QuestKind.Purge ||
    step.kind === QuestKind.Hold ||
    step.kind === QuestKind.Reach ||
    step.areaHint !== undefined;
  const area = needsArea
    ? (areas.find((a) => a.name === step.areaHint) ??
      areas[Math.floor(rng() * Math.max(1, areas.length))])
    : undefined;

  // A scripted chain is a script: literal goals, literal order, and no payout at all. The
  // tutorial's whole reward is granted once, on extraction, by the server.
  if (chain.scripted === true) {
    return {
      id: step.id,
      kind: step.kind,
      title: step.title,
      objective: step.objective
        .replace('{n}', String(step.goal))
        .replace('{area}', area?.name ?? defaultAreaName(mapDef)),
      goal: step.goal,
      target: step.target ?? 0,
      ...(step.itemId !== undefined ? { itemId: step.itemId } : {}),
      ...(area ? { area } : {}),
      ...(step.kind === QuestKind.Hold ? { timeLimitS: step.goal } : {}),
      reward: { wick: 0, xp: 0 },
      voice: step.voice,
      brief: step.brief,
      line: step.line,
      chainId: chain.id,
      stepIndex,
      ...(step.setPiece ? { setPiece: step.setPiece } : {}),
      scripted: true,
    };
  }

  const pf = playerFactor(playerCount);
  // Difficulty ramps inside the chain (+12% a step) and with how deep this expedition already is.
  const ramp = 1 + 0.12 * stepIndex + 0.06 * chainIndex;
  const scaled =
    step.kind === QuestKind.Hold
      ? clamp(Math.round(step.goal * (1 + 0.1 * stepIndex)), 30, 120)
      : step.kind === QuestKind.Silence
        ? clamp(Math.round(step.goal + stepIndex / 3), 1, 4)
        : clamp(Math.round(step.goal * ramp * pf), 1, 24);

  const base = questReward(chainIndex + stepIndex);
  const mult = step.setPiece ? SET_PIECE_REWARD_MULT : 1;

  const objective = step.objective
    .replace('{n}', String(scaled))
    .replace('{area}', area?.name ?? defaultAreaName(mapDef));

  return {
    id: step.id,
    kind: step.kind,
    title: step.title,
    objective,
    goal: scaled,
    target: step.target ?? 0,
    ...(area ? { area } : {}),
    ...(step.kind === QuestKind.Hold ? { timeLimitS: scaled } : {}),
    reward: { wick: base.wick * mult, xp: base.xp * mult },
    voice: step.voice,
    brief: step.brief,
    line: step.line,
    chainId: chain.id,
    stepIndex,
    ...(step.setPiece ? { setPiece: step.setPiece } : {}),
    rewardPool: questRewardPool(step.kind),
  };
}

// ------------------------------------------------------------------ geometry

/**
 * Is `p` inside `area`? Cylindrical: XZ radius plus a vertical half-height, so the Spine's stacked
 * decks (6 m apart) never bleed into each other.
 */
export function inArea(area: AreaDef, p: Vec3): boolean {
  const dx = p.x - area.pos.x;
  const dz = p.z - area.pos.z;
  if (dx * dx + dz * dz > area.radius * area.radius) return false;
  return Math.abs(p.y - area.pos.y) <= area.halfHeight;
}

/** Roman numeral for the quest badge ("QUEST II"). Chain index is 0-based. */
export function questNumeral(chainIndex: number): string {
  const n = chainIndex + 1;
  if (n > 3999) return String(n);
  const table: [number, string][] = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];
  let left = n;
  let out = '';
  for (const [v, s] of table) {
    while (left >= v) {
      out += s;
      left -= v;
    }
  }
  return out;
}
